import React from "react";
import cn from "classnames";
import { useI18N } from "@src/language/language-context";
import { type StaticTTMachineData } from "../../pages/factory-dashboard/static-tt-data";
import type { ModeColor } from "@src/types/factory-dashboard";
import styles from "./static-machine-overview-container-for-tt.module.scss";

type GridArea = "quarter" | "half";

interface StaticMachineOverviewContainerForTtProps {
  machine: StaticTTMachineData;
  gridArea: GridArea;
}

const cssClassName = "machine-overview-container";

const getModeBadgeStyle = (modeColor: ModeColor) => ({
  backgroundColor: modeColor.background,
  borderColor: modeColor.outline,
  color: modeColor.outline,
  outline: `1px solid ${modeColor.outline}`,
});

const getProgressPercentage = (progress: string): number => {
  const parsed = Number.parseFloat(progress.replace("%", "").trim());

  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return Math.max(0, Math.min(parsed, 100));
};

export const StaticMachineOverviewContainerForTt = ({
  machine,
  gridArea,
}: StaticMachineOverviewContainerForTtProps) => {
  const i18n = useI18N();

  const texts = {
    job: i18n("modules.staticMachineOverviewContainerForTt.job"),
    targetPartQuantityGoodTotal: i18n(
      "modules.staticMachineOverviewContainerForTt.targetPartQuantityGoodTotal",
    ),
    progress: i18n("modules.staticMachineOverviewContainerForTt.progress"),
    cycleTime: i18n("modules.staticMachineOverviewContainerForTt.cycleTime"),
    timeRemaining: i18n(
      "modules.staticMachineOverviewContainerForTt.timeRemaining",
    ),
    effectiveness: i18n(
      "modules.staticMachineOverviewContainerForTt.effectiveness",
    ),
    lastUpdated: i18n(
      "modules.staticMachineOverviewContainerForTt.lastUpdated",
    ),
    modeAutomatic: i18n(
      "modules.staticMachineOverviewContainerForTt.modeAutomatic",
    ),
    noDataAvailable: i18n(
      "modules.staticMachineOverviewContainerForTt.noDataAvailable",
    ),
  };

  const mode = machine?.modeMapping?.[machine?.modeValue];
  const modeLabel = mode?.label ? i18n(mode.label) : texts.modeAutomatic;
  const modeColor = mode?.color ?? { outline: "", background: "" };

  const hasData = [
    machine?.job,
    machine?.targetPartQuantityGoodTotal,
    machine?.progress,
    machine?.cycleTime,
    machine?.timeRemaining,
    machine?.effectiveness,
    machine?.lastUpdated,
  ].some((value) => value?.trim());

  return (
    <section
      className={cn(styles[cssClassName], {
        [styles[`${cssClassName}--grid-quarter`]]: gridArea === "quarter",
        [styles[`${cssClassName}--grid-half`]]: gridArea === "half",
      })}
    >
      {!hasData ? (
        <>
          <h3 className={styles[`${cssClassName}__title`]}>
            {machine.machineName}
          </h3>
          <div className={styles[`${cssClassName}__no-data`]}>
            <p className={styles[`${cssClassName}__no-data-text`]}>
              {texts.noDataAvailable}
            </p>
          </div>
        </>
      ) : (
        <>
          <h3 className={styles[`${cssClassName}__title`]}>
            {machine.machineName}
          </h3>

          <button
            className={styles[`${cssClassName}__badge`]}
            style={getModeBadgeStyle(modeColor)}
            type="button"
          >
            {modeLabel}
          </button>

          <div className={styles[`${cssClassName}__row`]}>
            <span className={styles[`${cssClassName}__key`]}>{texts.job}</span>
            <span className={styles[`${cssClassName}__value`]}>
              {machine.job}
            </span>
          </div>

          <div className={styles[`${cssClassName}__row`]}>
            <span className={styles[`${cssClassName}__key`]}>
              {texts.targetPartQuantityGoodTotal}
            </span>
            <span className={styles[`${cssClassName}__value`]}>
              {machine?.targetPartQuantityGoodTotal}
            </span>
          </div>

          <div className={styles[`${cssClassName}__progress-track`]}>
            <div
              className={styles[`${cssClassName}__progress-fill`]}
              style={{ width: `${getProgressPercentage(machine.progress)}%` }}
            />
          </div>

          <div className={styles[`${cssClassName}__row`]}>
            <span className={styles[`${cssClassName}__key`]}>
              {texts.progress}
            </span>
            <span className={styles[`${cssClassName}__value`]}>
              {machine?.progress}
            </span>
          </div>

          <div className={styles[`${cssClassName}__row`]}>
            <span className={styles[`${cssClassName}__key`]}>
              {texts.cycleTime}
            </span>
            <span className={styles[`${cssClassName}__value`]}>
              {machine?.cycleTime}
            </span>
          </div>

          <div className={styles[`${cssClassName}__row`]}>
            <span className={styles[`${cssClassName}__key`]}>
              {texts.timeRemaining}
            </span>
            <span className={styles[`${cssClassName}__value`]}>
              {machine?.timeRemaining}
            </span>
          </div>

          <div className={styles[`${cssClassName}__row`]}>
            <span className={styles[`${cssClassName}__key`]}>
              {texts.effectiveness}
            </span>
            <span className={styles[`${cssClassName}__value`]}>
              {machine.effectiveness}
            </span>
          </div>

          {machine.lastUpdated && (
            <p className={styles[`${cssClassName}__updated-at`]}>
              {texts.lastUpdated}: {machine.lastUpdated}
            </p>
          )}
        </>
      )}
    </section>
  );
};
